import { TGlobalStores } from '@/editor/stores';
import { EDirection, EOperateMode, EToolType } from '@/editor/types';
import { Box3, MOUSE, OrthographicCamera, Vector3 } from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { AbstractCommonController } from '../AbstractCommonController';
import editorEvent, { EditorEventName } from '@/editor/event/editorEvent';
import { getLeftPanelOverlapRatio } from '@/utils/editor/controller';
import { modelManager } from '@/editor/manager/entityManager';
import { expandByWallOutlines } from '@/editor/utils/scene';
import { clientPosToWorldPos } from '@/editor/utils';

export class CommonController2d extends OrbitControls implements AbstractCommonController {
  protected _domElement: HTMLElement;
  private _dragged: boolean = false; // 在mousedown到mouseup之间，鼠标是否移动过
  protected _exitOperateMode: EOperateMode = EOperateMode.EMPTY;
  protected _keyboardMoveUpdate: (() => void) | null = null;

  constructor(protected _gs: TGlobalStores) {
    const camera = _gs.editorStateStore.sceneInfo.scene2d.camera;
    const canvasElement = _gs.editorStateStore.sceneInfo.canvasElement;
    super(camera, canvasElement);

    this._domElement = canvasElement;

    this.mouseButtons = {
      MIDDLE: MOUSE.PAN, // 中键平移
      RIGHT: MOUSE.PAN // 右键平移
    };
    this.enableRotate = false; // 禁止旋转
    this.screenSpacePanning = true; // 平移方式
    this.zoomToCursor = true;
    this.minZoom = 0.01;

    // 初始化目标点
    this.target.set(camera.position.x, camera.position.y, 0);
    this.update();
  }

  get zoomLevel() {
    return this._gs.editorStateStore.sceneInfo.scene2d.camera.zoom * 100;
  }

  get exitOperateMode() {
    return this._exitOperateMode;
  }

  set exitOperateMode(mode: EOperateMode) {
    this._exitOperateMode = mode;
  }

  init() {
    this._domElement.addEventListener('mousedown', this.commonMouseDown);
    this._domElement.addEventListener('mousemove', this.commonMouseMove);
    this._domElement.addEventListener('mouseup', this.commonMouseUp);

    this.addEventListener('change', this.handleViewChange);
    editorEvent.on(EditorEventName.ZOOM_UPDATE_2D, this.updateZoomLevel);
    editorEvent.on(EditorEventName.RESIZE, this.handleResize);

    // 初始化时调整画布大小和相机参数以适应当前窗口，避免在3D调整大小后切换到2D时画布显示异常
    this.handleResize();
  }

  private commonMouseDown = (e: MouseEvent) => {
    this._dragged = false;
    this.mouseDown(e);
  };

  private commonMouseMove = (e: MouseEvent) => {
    // 右键或中键时，被认为是拖拽
    if (e.buttons === 2 || e.buttons === 4) this._dragged = true;
    this.mouseMove(e);
  };

  private commonMouseUp = (e: MouseEvent) => {
    this.mouseUp(e, this._dragged);
  };

  protected mouseDown(_e: MouseEvent): void {}
  protected mouseMove(_e: MouseEvent): void {}
  protected mouseUp(_e: MouseEvent, _dragged: boolean): void {}

  private updateZoomLevel = (zoom: number) => {
    if (!isNaN(zoom) && zoom > 0 && zoom !== this.zoomLevel) {
      const { camera } = this._gs.editorStateStore.sceneInfo.scene2d;
      camera.zoom = zoom / 100;
      camera.updateProjectionMatrix();
      this.handleViewChange();
      this.update();
    }
  };

  handleViewChange = () => {
    this._gs.scene2dZoom = this.zoomLevel;
    editorEvent.emit(EditorEventName.VIEW2D_CHANGE);
    this._gs.editorStateStore.setZooming();
  };

  handleResize = () => {
    const camera = this._gs.editorStateStore.sceneInfo.scene2d.camera;
    const renderer = this._gs.editorStateStore.sceneInfo.renderer;

    const domWidth = this._domElement.clientWidth;
    const domHeight = this._domElement.clientHeight;
    const aspect = domWidth / domHeight;

    // 更新渲染器
    renderer.setSize(domWidth, domHeight); // TODO: 需要限制下最大可渲染分辨率

    // 更新相机 - 保持内容比例不变
    const frustumHeight = camera.top - camera.bottom;
    const frustumWidth = frustumHeight * aspect;

    camera.left = -frustumWidth / 2;
    camera.right = frustumWidth / 2;
    camera.top = frustumHeight / 2;
    camera.bottom = -frustumHeight / 2;
    camera.updateProjectionMatrix();

    this.update();
  };

  /**
   * 基于墙体和底图的画布适应，会考虑左侧栏的遮挡
   */
  fitToView() {
    const originalCamera = this._gs.editorStateStore.sceneInfo.scene2d.camera;
    if (originalCamera instanceof OrthographicCamera === false) {
      return;
    }

    const contentBoundingBox = new Box3();
    const cameraPos = originalCamera.position.clone();
    const targetPos = this.target.clone();
    let zoomLevel = 100;

    // 将墙体数据加入边界计算
    expandByWallOutlines(contentBoundingBox);

    // 将底图轮廓加入边界计算
    contentBoundingBox.union(modelManager.floorPlanManager.getBoundingBox());

    if (contentBoundingBox.isEmpty()) {
      cameraPos.set(0, 0, cameraPos.z);
      targetPos.set(0, 0, targetPos.z);
    } else {
      const cameraSize = {
        width: Math.abs(originalCamera.right - originalCamera.left),
        height: Math.abs(originalCamera.top - originalCamera.bottom)
      };
      const center = contentBoundingBox.getCenter(new Vector3());
      const size = contentBoundingBox.getSize(new Vector3());
      const padding = Math.max(size.x, size.y) * 0.05;

      // 计算左侧面板遮挡的宽度
      const panelOverlapWidth = cameraSize.width * getLeftPanelOverlapRatio(this._domElement as HTMLCanvasElement);

      zoomLevel =
        Math.min(
          (cameraSize.width - panelOverlapWidth) / (size.x + padding),
          cameraSize.height / (size.y + padding),
          1
        ) * 100;

      const offsetX = panelOverlapWidth > 0 ? panelOverlapWidth / 2 / (zoomLevel / 100) : 0;

      center.set(center.x - offsetX, center.y, center.z);
      cameraPos.set(center.x, center.y, cameraPos.z);
      targetPos.set(center.x, center.y, targetPos.z);
    }

    originalCamera.position.copy(cameraPos);
    this.target.copy(targetPos);
    this.updateZoomLevel(zoomLevel);
    this.update();
  }

  public keyboardMoveUpdate() {
    if (this._keyboardMoveUpdate) {
      this._keyboardMoveUpdate();
      this.update();
    }
  }

  public moveEntityByDirection(_direction: EDirection) {}

  public flushSync() {}

  protected getMousePos(e: MouseEvent) {
    const {
      renderer,
      scene2d: { camera }
    } = this._gs.editorStateStore.sceneInfo;

    return clientPosToWorldPos(e, camera, renderer);
  }

  // 销毁监听，退出该控制器
  override dispose() {
    editorEvent.off(EditorEventName.ZOOM_UPDATE_2D, this.updateZoomLevel);
    editorEvent.off(EditorEventName.RESIZE, this.handleResize);

    this._domElement.removeEventListener('mousedown', this.commonMouseDown);
    this._domElement.removeEventListener('mousemove', this.commonMouseMove);
    this._domElement.removeEventListener('mouseup', this.commonMouseUp);

    this.removeEventListener('change', this.handleViewChange);

    super.dispose();
  }

  // 控制器内部退出时调用
  protected exit() {
    this._gs.operateInfo = { mode: this._exitOperateMode, toolType: EToolType.empty };
  }
}
